import { queryOptions } from '@tanstack/react-query';
import type { PageProblems } from '../../../packages/shared/src/problems';
import type { Locale } from './i18n';

export type Role = 'owner' | 'admin' | 'editor' | 'viewer';

export interface Me {
  user: { id: string; name: string; email: string; locale: Locale; twoFactorEnabled: boolean };
  /** The session's active organisation, with the user's role and what it allows. */
  organization: {
    id: string;
    name: string;
    role: Role;
    permissions: Record<string, string[]>;
  } | null;
  organizations: { id: string; name: string }[];
  /** The user is a superadmin: sees every user and assigns plans. */
  superadmin: boolean;
  plan: Plan;
}

export const PLANS = ['gratis', 'pro', 'agencia', 'a-medida', 'infinita'] as const;
export type Plan = (typeof PLANS)[number];

export interface AdminUser {
  id: string;
  name: string;
  email: string;
  emailVerified: boolean;
  createdAt: string;
  organizations: { id: string; name: string; role: Role }[];
  plan: Plan;
  planUpdatedAt: string | null;
  /** Websites the user added and scans the user launched. */
  sites: number;
  scans: number;
  /** WordPress installations linked by the user that are still live. */
  wpConnections: number;
  lastSeenAt: string | null;
  banned: boolean;
  banReason: string | null;
  /** Superadmins cannot be blocked. */
  superadmin: boolean;
}

export const OVERVIEW_DAYS = [7, 30, 90] as const;
export type OverviewDays = (typeof OVERVIEW_DAYS)[number];

/** Sign-ups per day (Madrid days, oldest first) and how far the accounts got. */
export interface AdminOverview {
  funnel: {
    signedUp: number;
    verified: number;
    withOrganization: number;
    withSite: number;
    withScan: number;
  };
  banned: number;
  signups: { last7: number; previous7: number; last30: number };
  daily: { date: string; signups: number; waitlist: number }[];
  waitlist: { total: number; last30: number };
  freeAudits: { total: number; last30: number };
}

/** `sin-definir`: what a new website has until somebody chooses; it is never deduced from the domain. */
export type RegulatoryProfile = 'eaa' | 'sector-publico' | 'voluntario' | 'sin-definir';
export type ChoosableProfile = Exclude<RegulatoryProfile, 'sin-definir'>;
export const REGULATORY_PROFILES: ChoosableProfile[] = ['eaa', 'sector-publico', 'voluntario'];
/** The EN 301 549 version the Official Journal of the EU cites today (Decision (EU) 2021/1339). Change it
 *  when v4.1.1 is cited there; the website's own standard comes from its regulatory profile. */
export const HARMONISED_STANDARD = 'EN301549-3.2.1';
export type VerificationMethod = 'meta' | 'dns' | 'fichero';
export const VERIFICATION_METHODS: VerificationMethod[] = ['meta', 'dns', 'fichero'];

export interface Site {
  id: string;
  name: string;
  url: string;
  host: string;
  regulatoryProfile: RegulatoryProfile;
  targetStandard: string;
  /** Added in the dashboard or linked by the WordPress plugin. */
  source: 'panel' | 'wordpress';
  createdAt: string;
  verification: {
    status: 'unverified' | 'checking' | 'verified' | 'failed';
    method: VerificationMethod | null;
    verifiedAt: string | null;
    checkedAt: string | null;
    /** meta-missing, file-missing, dns-missing, http:<status>, unreachable or timeout. */
    error: string | null;
    token: string;
    meta: string;
    dns: { name: string; type: string; value: string };
    file: { url: string; content: string };
  };
}

/** An API answer other than 2xx; `code` is the API's error code (url, duplicada, permiso…). */
export class ApiError extends Error {
  constructor(
    readonly status: number,
    readonly code: string | null = null,
    /** The rest of the answer (e.g. the project that already exists for a duplicate address). */
    readonly data: unknown = null,
  ) {
    super(`HTTP ${status}${code ? ` (${code})` : ''}`);
  }
}

async function api<T>(path: string, init: { method?: string; body?: unknown } = {}): Promise<T> {
  const res = await fetch(path, {
    method: init.method ?? 'GET',
    headers: {
      accept: 'application/json',
      ...(init.body === undefined ? {} : { 'content-type': 'application/json' }),
    },
    body: init.body === undefined ? undefined : JSON.stringify(init.body),
  });
  if (res.status === 204) return undefined as T;
  const data: unknown = await res.json().catch(() => null);
  if (!res.ok) {
    const code = (data as { error?: unknown } | null)?.error;
    throw new ApiError(res.status, typeof code === 'string' ? code : null, data);
  }
  return data as T;
}

/** The signed-in user, or null without a session. */
export const meQuery = queryOptions({
  queryKey: ['me'],
  queryFn: () => api<Me | null>('/api/app/me'),
  staleTime: 30_000,
});

/** What the sign-in screens may offer. Public: it is asked before there is a session. */
export const authOptionsQuery = queryOptions({
  queryKey: ['auth-options'],
  queryFn: () => api<{ google: boolean }>('/api/auth-options').catch(() => ({ google: false })),
  staleTime: Number.POSITIVE_INFINITY,
});

/** Whether the user's role allows an action in the active organisation (the API checks it too). */
export function can(me: Me | null | undefined, resource: string, action: string): boolean {
  return Boolean(me?.organization?.permissions[resource]?.includes(action));
}

export const sitesQuery = queryOptions({
  queryKey: ['sites'],
  queryFn: () => api<{ sites: Site[] }>('/api/app/sites').then((data) => data.sites),
});

export const siteQuery = (id: string) =>
  queryOptions({
    queryKey: ['sites', id],
    queryFn: () => api<Site>(`/api/app/sites/${encodeURIComponent(id)}`),
    // While the worker checks the ownership, ask again every couple of seconds.
    refetchInterval: (query) => (query.state.data?.verification.status === 'checking' ? 2000 : false),
  });

/** A project of the active organisation, as the form to add a website offers it. */
export interface ExistingProject {
  id: string;
  name: string;
}

/** The normalised address (null when it is not valid) and the project that already has it, if any. */
export const lookupSite = (url: string) =>
  api<{ url: string | null; existing: ExistingProject | null }>('/api/app/sites/lookup', {
    method: 'POST',
    body: { url },
  });

/** Creates the project only: no analysis starts and none is used up. */
export const createSite = (input: { url: string; name?: string }) =>
  api<Site>('/api/app/sites', { method: 'POST', body: input });

export const updateSite = (id: string, patch: { name?: string; regulatoryProfile?: ChoosableProfile }) =>
  api<Site>(`/api/app/sites/${encodeURIComponent(id)}`, { method: 'PATCH', body: patch });

export const deleteSite = (id: string) =>
  api<void>(`/api/app/sites/${encodeURIComponent(id)}`, { method: 'DELETE' });

/** Take the card picture and the icon again (no analysis, no pages of the plan). */
export const refreshCardImage = (id: string) =>
  api<{ ok: true }>(`/api/app/sites/${encodeURIComponent(id)}/card-image`, { method: 'POST', body: {} });

export const requestVerification = (id: string, method: VerificationMethod) =>
  api<Site>(`/api/app/sites/${encodeURIComponent(id)}/verification`, { method: 'POST', body: { method } });

export type Severity = 'critica' | 'grave' | 'moderada' | 'leve';
export const SEVERITIES: Severity[] = ['critica', 'grave', 'moderada', 'leve'];
/** `pagina`: a single page, the one the website was added with. */
export type ScanType = 'pagina' | 'rapido' | 'completo' | 'personalizado' | 'comprobacion';
/** What a person can ask for from the dashboard. */
export type ScanMode = 'pagina' | 'rapido' | 'completo';

/** Issues by kind, as the scan summary splits them (also per page, with its affected elements). */
export interface IssueCounts {
  accessibility: { violations: Record<Severity, number>; review: number; bestPractices: number };
  seo: number;
  quality: number;
}
export interface PageCounts extends IssueCounts {
  nodes: number;
}

export interface Scores {
  /** score = 60 · automated + 40 · manual (both from 0 to 1); the band stays "revision-pendiente"
   *  until the manual review covers the criteria. */
  accesibilidad: { score: number | null; band: string; automated?: number | null; manual?: number };
  seo: { score: number | null };
  calidad: { score: number | null };
}

/** The five states of a scan, as the API derives them from what the engine stored (packages/shared/src/outcome.ts). */
export type ScanOutcome = 'queued' | 'running' | 'complete' | 'partial' | 'failed';
/** Why a scan could not be completed, only when the system knows it. */
export type FailureCause = 'interrupted' | 'domain' | 'limited' | 'blocked' | 'unreachable' | 'unknown';

export interface Scan {
  id: string;
  siteId: string;
  type: ScanType;
  status: 'queued' | 'running' | 'done' | 'failed';
  outcome: ScanOutcome;
  cause: FailureCause | null;
  /** Pages the engine tried and could not analyse; null until it reports them. */
  pagesNotAnalysed: number | null;
  /** The most the scan may use and what it used (null until it ends). */
  consumption: { planned: number; used: number | null };
  /** True when the person chose the addresses (left out of the estimate trend). */
  customPages?: boolean;
  /** The partial full scan this one is completing, when it is a completion. */
  parentScanId?: string | null;
  createdAt: string;
  startedAt: string | null;
  finishedAt: string | null;
  progress: { done: number; total: number | null };
  /** Null while it runs; a score is null when no page could be analysed. */
  scores: Scores | null;
  stats: {
    pagesScanned: number;
    pagesBlocked: number;
    /** Blocked pages the website rate-limited (also counted in pagesBlocked); absent in older scans. */
    pagesLimited?: number;
    /** Pages analysed while the website refused some requests; absent in older scans. */
    pagesPartial?: number;
    pagesFailed: number;
  } | null;
  summary: IssueCounts | null;
  error: string | null;
}

export type Device = 'escritorio' | 'movil';
export type CaptureKind = Device | 'miniatura';

export interface ScannedPage {
  /** The engine's id for the page (also in the screenshots' names). */
  key: string;
  url: string;
  finalUrl: string;
  status: number | null;
  title: string | null;
  blocked: boolean;
  blockedReason: string | null;
  error: string | null;
  lang: string | null;
  redirects: number;
  timings: { ttfbMs: number | null; loadMs: number | null };
  requests: number | null;
  bytes: number | null;
  features: { video: boolean; audio: boolean; forms: boolean; iframes: boolean } | null;
  /** Null for pages that could not be analysed, and for scans saved before the counts existed. */
  counts: PageCounts | null;
  /** The screenshots it has (only the two latest scans of a website keep them). */
  captures: Record<CaptureKind, boolean>;
}

/** A page of a running scan, once analysed (the live view). */
export interface LivePage {
  key: string;
  url: string;
  title: string | null;
  status: number | null;
  blocked: boolean;
  error: string | null;
  counts: PageCounts;
  thumbnail: boolean;
}

export interface LiveScan {
  done: number;
  total: number;
  /** Addresses being analysed right now. */
  current: string[];
  pages: LivePage[];
}

export interface ScanComparison {
  previous: { id: string; createdAt: string; score: number | null };
  newIssues: number;
  resolvedIssues: number;
}

/** An automated check that applied to the pages analysed and found nothing (it is not a pass: see the manual review). */
export interface PassedCheck {
  ruleId: string;
  title: string;
  description: string;
  kind: string;
  criteria: string[];
  /** The WCAG criteria it looks at, with their names and levels. */
  criteriaDetail: { id: string; title: string; level: 'A' | 'AA' | null }[];
  /** Pages (of those analysed) where it applied. */
  pages: number;
}

export interface ScanChecks {
  /** False for scans saved before the engine kept them. */
  available: boolean;
  analysedPages: number;
  passed: PassedCheck[];
}

/** Where a criterion stands for manual review. `pendiente` also covers "no automated test reaches it". */
export type ManualStatus = 'incumple' | 'sin-incidencias' | 'pendiente';

export interface ManualCriterion {
  /** Where the manual review of the criterion stands: apart from the automatic status below. */
  review: CriterionReview;
  id: string;
  title: string;
  summary: string;
  level: 'A' | 'AA';
  principle: string;
  principleName: string;
  status: ManualStatus;
  /** Confirmed automated problems that touch this criterion, and automated items to review. */
  problems: number;
  toReview: number;
}

// The manual checks (packages/shared/src/manual-review.ts): a person follows the guide of a criterion and records
// what they found. Saving says it was done, never that it was passed.
export type ReviewAnswer = 'sin-problemas' | 'problema' | 'no-comprobado' | 'no-aplica';
export const REVIEW_ANSWERS: ReviewAnswer[] = ['sin-problemas', 'problema', 'no-comprobado', 'no-aplica'];
export type ReviewStatus = 'borrador' | 'registrada';
export type ReviewDevice = 'escritorio' | 'movil' | 'ambos';
export type ReviewSeverity = 'grave' | 'moderada' | 'leve';
/** `guiada`: anybody can follow the steps. `especializada`: it can need help from somebody who knows the tools. */
export type ManualHelp = 'guiada' | 'especializada';
/** Where an aspect stands: not started, pending (a draft) or registered. */
export type ReviewState = 'no-iniciada' | 'pendiente' | 'registrada';

export const CONDITIONS = [
  'teclado',
  'lector-de-pantalla',
  'zoom',
  'espaciado-de-texto',
  'movil',
  'colores-forzados',
  'escala-de-grises',
  'sin-estilos',
  'sin-sonido',
  'reducir-movimiento',
  'voz',
] as const;
export type Condition = (typeof CONDITIONS)[number];

/** What a criterion looks like in the list of manual checks. */
export interface CriterionReview {
  /** Null: there is no guide, and the screen says so. */
  guide: { help: ManualHelp; scope: 'page' | 'site'; lang: Locale } | null;
  state: ReviewState;
  reviews: number;
  drafts: number;
  answer: ReviewAnswer | null;
  answers: Record<ReviewAnswer, number>;
  answeredAt: string | null;
  by: Person | null;
  pagesReviewed: number;
  /** A page that was reviewed changed since: the earlier valuation stays, a new review is needed. */
  needsNew: boolean;
}

export interface ManualProgress {
  total: number;
  started: number;
  registered: number;
  needNew: number;
  answers: Record<ReviewAnswer, number>;
}

export interface ManualReview {
  standard: string;
  total: number;
  /** Criteria with a registered review (on some page). */
  registered: number;
  progress: ManualProgress;
  criteria: ManualCriterion[];
}

/** The guide a review followed, as it was when it started. */
export interface ManualInstructions {
  criterion: string;
  title: string;
  lang: Locale;
  version: number;
  help: ManualHelp;
  scope: 'page' | 'site';
  what: string;
  how: string[];
  expected: string[];
  record: string[];
  specialistNote?: string;
}

export interface ReviewView {
  id: string;
  criterion: string;
  status: ReviewStatus;
  answer: ReviewAnswer | null;
  page: { key: string; url: string; title: string | null } | null;
  element: string | null;
  device: ReviewDevice;
  conditions: { ids: Condition[]; notes?: string };
  description: string | null;
  justification: string | null;
  evidence: string | null;
  severity: ReviewSeverity | null;
  priority: Priority | null;
  instructions: ManualInstructions;
  guideVersion: number;
  by: Person | null;
  createdAt: string;
  updatedAt: string;
  registeredAt: string | null;
  previousId: string | null;
  occurrenceId: string | null;
  /** The latest registered review of its criterion, page, element and device. */
  current: boolean;
  needsNew: boolean;
}

export interface GuideDetail {
  criterion: {
    id: string;
    title: string;
    summary: string;
    level: 'A' | 'AA';
    principle: string;
    principleName: string;
  };
  /** Null when there is no guide for the criterion. */
  guide: (ManualInstructions & { lang: Locale }) | null;
  suggestedSeverity: ReviewSeverity;
  scanId: string | null;
  /** The pages the latest analysis loaded and analysed: the ones a review can be about. */
  pages: { key: string; url: string; title: string | null }[];
  /** Registered reviews, newest first, and the requester's own drafts. */
  reviews: ReviewView[];
  drafts: ReviewView[];
}

export const manualGuideQuery = (siteId: string, criterion: string) =>
  queryOptions({
    queryKey: ['sites', siteId, 'manual', criterion, lang()],
    queryFn: () =>
      api<GuideDetail>(
        `/api/app/sites/${encodeURIComponent(siteId)}/manual/${encodeURIComponent(criterion)}?lang=${lang()}`,
      ),
    staleTime: 0,
  });

export interface ReviewPatch {
  answer?: ReviewAnswer | null;
  pageKey?: string | null;
  element?: string | null;
  device?: ReviewDevice;
  conditions?: { ids: Condition[]; notes?: string };
  description?: string | null;
  justification?: string | null;
  evidence?: string | null;
  severity?: ReviewSeverity | null;
}

/** Starts a review, or resumes the draft of the same page, element and device. */
export const startReview = (
  siteId: string,
  criterion: string,
  body: {
    pageKey?: string | null;
    element?: string | null;
    device?: ReviewDevice;
    occurrenceId?: string;
    previousId?: string;
  },
) =>
  api<ReviewView & { resumed: boolean }>(
    `/api/app/sites/${encodeURIComponent(siteId)}/manual/${encodeURIComponent(criterion)}/reviews?lang=${lang()}`,
    { method: 'POST', body },
  );

export const saveReview = (id: string, patch: ReviewPatch) =>
  api<ReviewView>(`/api/app/manual-reviews/${encodeURIComponent(id)}`, { method: 'PATCH', body: patch });

export const registerReview = (id: string, patch: ReviewPatch) =>
  api<ReviewView>(`/api/app/manual-reviews/${encodeURIComponent(id)}/register`, {
    method: 'POST',
    body: patch,
  });

export const discardDraft = (id: string) =>
  api<void>(`/api/app/manual-reviews/${encodeURIComponent(id)}`, { method: 'DELETE' });

/** What a check of corrections found, element by element: it is not an analysis of the whole website. */
export interface CheckedByScan {
  pages: number;
  elements: number;
  verified: number;
  persists: number;
  /** Not decided: it could not be checked, or a person has to review it. */
  pending: number;
  items: {
    id: string;
    ruleTitle: string;
    element: ElementName;
    page: { key: string; url: string };
    devices: Device[];
    check: FollowState['check'];
  }[];
}

export interface ScanDetail extends Scan {
  /** What the engine found; `scores` includes the decisions made since (what was discarded). */
  engineScores: Scan['scores'];
  pages: ScannedPage[];
  capturesAvailable: boolean;
  live: LiveScan | null;
  comparison: ScanComparison | null;
  /** Only for a check of corrections: what it looked at and what each check decided. */
  checked: CheckedByScan | null;
  /** Null until the scan has a result. */
  checks: ScanChecks | null;
  manual: ManualReview | null;
  /** Elements the team discarded with a justification: they are out of the counts and the score. */
  discarded: number;
}

/** One criterion for every screen: how much a problem hinders people, or "pending review" when it is not decided. */
export type Priority = 'alta' | 'media' | 'baja' | 'pendiente';
export const PRIORITIES: Priority[] = ['alta', 'media', 'baja', 'pendiente'];

/** What an issue amounts to on one page, counted once and without the example cap. */
export interface PageStat {
  /** Distinct elements: one that fails on desktop and on mobile counts once. */
  elements: number;
  /** Every time the check found one (so desktop and mobile add up). */
  appearances: number;
  devices: ('escritorio' | 'movil')[];
  /** The engine stopped reporting elements: the figures are "at least". */
  capped: boolean;
  /** Elements discarded with a justification: they are not in the figures above. */
  discarded: number;
  /** Elements of a shared component that another page looks after: this page does not count them as its own. */
  delegated?: number;
  /** Elements of a shared component this page looks after (for itself and for the other pages that have it). */
  global?: number;
}

export interface ScanIssue {
  id: string;
  ruleId: string;
  module: 'accesibilidad' | 'seo' | 'calidad';
  kind: string;
  result: 'violation' | 'incomplete';
  severity: Severity;
  priority: Priority;
  criteria: string[];
  criteriaDetail: { id: string; title: string; level: 'A' | 'AA' | null }[];
  title: string;
  description: string | null;
  fix: string | null;
  /** Who the problem affects most, in the language asked for. */
  affects: { id: string; label: string }[];
  /** Who can usually fix it, by the kind of problem (not by what we know of the person). */
  roles: ('desarrollo' | 'diseno' | 'contenido')[];
  /** Found by the engine (`automatico`) or by a person following a guide (`manual`). */
  origin: 'automatico' | 'manual';
  devices: ('escritorio' | 'movil')[];
  /** Across the scan: distinct elements, appearances and whether they are "at least". */
  elements: number;
  appearances: number;
  capped: boolean;
  nodeCount: number;
  pageCount: number;
  /** By page key. */
  pageStats: Record<string, PageStat>;
  pageKeys: string[];
  pages: string[];
  /** The languages of the pages it is on (primary subtag of each page's `lang`) and how many pages do not say. */
  languages: { codes: string[]; undeclared: number };
  /** The same element (selector and structure) on several pages: probably one shared component. */
  shared: SharedComponent[];
  /** Per page, what the team follows of this issue there. Each page is settled on its own. */
  pageFollow: Record<string, { tracked: number; work: Record<string, number> }>;
  examples: {
    /** The id of this element on its page (see PageOccurrence), to open it there. */
    id: string;
    url: string | null;
    pageKey: string;
    device: string;
    selector: string;
    xpath: string | null;
    html: string;
    message: string | null;
    /** How a person names it, with where it sits on the page. */
    element: ElementName;
  }[];
  firstSeenAt: string;
  /** Elements the team discarded with a justification (the figures above leave them out). */
  discarded: number;
  /** For a problem found in a manual review: the review it comes from. */
  manual?: {
    reviewId: string;
    criterion: string;
    guide: boolean;
    element: string | null;
    device: ReviewDevice;
    conditions: { ids: Condition[]; notes?: string };
    evidence: string | null;
    by: Person | null;
    at: string | null;
    needsNew: boolean;
  };
  /** What the team follows among the elements of this problem. */
  followUp: {
    tracked: number;
    work: Record<WorkStatus, number>;
    confirmed: number;
    discarded: number;
    /** What the checks found on the elements the team follows. */
    check: { verified: number; persists: number; pending: number };
  };
}

// The follow-up of the elements (packages/shared/src/follow-up.ts): the work, the validation of the
// detection and the result of the check are three separate things.
export type WorkStatus = 'pendiente' | 'en-curso' | 'aplicada' | 'aplazada' | 'archivada';
/** The most pages one check of corrections analyses (packages/shared/src/usage.ts). */
export const MAX_CHECK_PAGES = 10;
export const WORK_STATUSES: WorkStatus[] = ['pendiente', 'en-curso', 'aplicada', 'aplazada', 'archivada'];
/** The ones a person picks in the form. */
export const CHOOSABLE_WORK: WorkStatus[] = ['pendiente', 'en-curso', 'aplicada', 'aplazada'];
export type ValidationStatus = 'pendiente' | 'confirmada' | 'descartada';
export type CheckResult = 'sigue-detectandose' | 'no-detectado' | 'no-comprobable' | 'requiere-revision';
export const CHECK_RESULTS: CheckResult[] = [
  'sigue-detectandose',
  'no-detectado',
  'no-comprobable',
  'requiere-revision',
];
export type CheckReason =
  | 'detectado'
  | 'detectado-en-revision'
  | 'comprobado'
  | 'revision-favorable'
  | 'revision-desfavorable'
  | 'pagina-fuera'
  | 'pagina-no-analizada'
  | 'dispositivo'
  | 'versiones'
  | 'regla-no-aplicada'
  | 'muestra'
  | 'posible-cambio'
  | 'ventana-emergente'
  | 'sin-registro'
  | 'incierta';

export interface Person {
  id: string;
  name: string;
}

/** Why work cannot be archived yet. */
export type ArchiveWhy = 'ya-archivada' | 'sin-comprobacion' | 'no-favorable' | 'anterior-a-la-correccion';

export interface FollowState {
  /** Null while nobody has recorded anything about the element. */
  id: string | null;
  work: { status: WorkStatus; note: string | null; by: Person | null; at: string | null };
  validation: {
    status: ValidationStatus | null;
    /** A person decided it (false: it is what the engine's result implies). */
    decided: boolean;
    justification: string | null;
    evidence: string | null;
    by: Person | null;
    at: string | null;
  };
  check: {
    result: CheckResult;
    source: 'automatica' | 'manual';
    reason: CheckReason;
    at: string;
    scanId: string | null;
    devices: Device[];
    note: string | null;
    by: Person | null;
  } | null;
  appliedAt: string | null;
  archive: {
    at: string;
    by: Person | null;
    evidence: {
      source: 'automatica' | 'manual';
      reason: CheckReason;
      at: string;
      scanId: string | null;
    } | null;
  } | null;
  canArchive:
    | {
        ok: true;
        evidence: { source: 'automatica' | 'manual'; reason: CheckReason; at: string; scanId: string | null };
      }
    | { ok: false; why: ArchiveWhy };
  lastDetectedAt: string | null;
  /** The guides of the manual checks that can repeat this check, and whether a person has to judge it. */
  manual: { guides: string[]; needsHuman: boolean };
}

export type FollowEventKind =
  | 'creada'
  | 'seguimiento'
  | 'validacion'
  | 'comprobacion'
  | 'revision-manual'
  | 'archivo'
  | 'reapertura';

export interface FollowEvent {
  id: string;
  kind: FollowEventKind;
  at: string;
  by: Person | null;
  detail: Record<string, unknown>;
}

/** An element the team follows, as the website's follow-up lists it. */
export interface FollowItem extends FollowState {
  id: string;
  ruleId: string;
  ruleTitle: string;
  element: ElementName;
  page: { key: string; url: string };
  devices: Device[];
  result: 'violation' | 'incomplete';
  scanId: string | null;
  /** The id of the element in the latest analysis, when that one lists it (to open it on its page). */
  locatedAs: string | null;
}

export interface FollowSummary {
  total: number;
  work: Record<WorkStatus, number>;
  validation: { pendiente: number; confirmada: number; descartada: number; sinDecidir: number };
  checks: Record<CheckResult, number> & { sinComprobar: number };
  items: FollowItem[];
  /** The latest finished analysis. */
  scanId: string | null;
}

export const followUpQuery = (siteId: string) =>
  queryOptions({
    queryKey: ['sites', siteId, 'follow-up', lang()],
    queryFn: () =>
      api<FollowSummary>(`/api/app/sites/${encodeURIComponent(siteId)}/follow-up?lang=${lang()}`),
  });

export const historyQuery = (occurrenceId: string) =>
  queryOptions({
    queryKey: ['occurrences', occurrenceId, 'history'],
    queryFn: () =>
      api<{ events: FollowEvent[] }>(`/api/app/occurrences/${encodeURIComponent(occurrenceId)}/history`).then(
        (data) => data.events,
      ),
    staleTime: 0,
  });

/** What a person asks to change; the rest stays as it is. */
export interface StateChange {
  work?: Exclude<WorkStatus, 'archivada'>;
  validation?: ValidationStatus;
  note?: string;
  justification?: string;
  evidence?: string;
}

/** Which elements a change is for: some of a scan's elements, every element of some of its problems, or one the team follows already. */
export type StateTarget = { occurrenceIds: string[] } | { issueIds: string[] } | { row: string };

/** One element: as an analysis lists it, or as the team follows it (a fixed one is not in any list). */
export type ElementRef = { scanId: string; occurrenceId: string } | { row: string };

/** Records a state on elements of a scan. It starts no analysis and uses no page. */
export const changeState = (scanId: string, target: StateTarget, change: StateChange) =>
  'row' in target
    ? api<{ updated: number; occurrences: FollowState[] }>(
        `/api/app/occurrences/${encodeURIComponent(target.row)}/state`,
        { method: 'PUT', body: change },
      )
    : api<{ updated: number; occurrences: FollowState[] }>(
        `/api/app/scans/${encodeURIComponent(scanId)}/states`,
        { method: 'PUT', body: { target, ...change } },
      );

export const reviewElement = (
  element: ElementRef,
  input: { outcome: 'favorable' | 'desfavorable'; devices: Device[]; note: string },
) =>
  api<FollowState>(
    'row' in element
      ? `/api/app/occurrences/${encodeURIComponent(element.row)}/review`
      : `/api/app/scans/${encodeURIComponent(element.scanId)}/elements/${encodeURIComponent(element.occurrenceId)}/review`,
    { method: 'POST', body: input },
  );

export const archiveWork = (occurrenceId: string, note: string) =>
  api<FollowState>(`/api/app/occurrences/${encodeURIComponent(occurrenceId)}/archive`, {
    method: 'POST',
    body: { note: note.trim() || undefined },
  });

const isActive = (scan: Scan | undefined) => scan?.status === 'queued' || scan?.status === 'running';

export const scansQuery = (siteId: string) =>
  queryOptions({
    queryKey: ['sites', siteId, 'scans'],
    queryFn: () =>
      api<{ scans: Scan[] }>(`/api/app/sites/${encodeURIComponent(siteId)}/scans`).then((data) => data.scans),
    // Follow the scan in progress.
    refetchInterval: (query) => (isActive(query.state.data?.[0]) ? 3000 : false),
    // Without a connection the request still goes out and fails, so the screen can say the state could
    // not be updated instead of showing an old one as if it were current.
    networkMode: 'always',
  });

/** The language of the dashboard, as the API takes it (`?lang=`): the texts of the results come in it. */
const lang = (): Locale => {
  // i18n.ts keeps <html lang> equal to the language in use; reading it here keeps this module free of the i18n set-up.
  const current = typeof document === 'undefined' ? 'es' : document.documentElement.lang;
  return (['es', 'en', 'fr', 'de'].includes(current) ? current : 'es') as Locale;
};

export const scanQuery = (scanId: string) =>
  queryOptions({
    queryKey: ['scans', scanId, 'detail', lang()],
    queryFn: () => api<ScanDetail>(`/api/app/scans/${encodeURIComponent(scanId)}?lang=${lang()}`),
    // The live view: often enough to see each page arrive.
    refetchInterval: (query) => (isActive(query.state.data) ? 2000 : false),
    networkMode: 'always',
  });

export interface Box {
  x: number;
  y: number;
  width: number;
  height: number;
}

export type ZoneId = 'header' | 'navigation' | 'main' | 'footer' | 'aside' | 'search' | 'dialog';

/** An element as a person recognises it: "Enlace «Foc»", in the "pie de página". */
export interface ElementName {
  kind: string;
  kindLabel: string;
  text: string | null;
  zone: ZoneId | null;
  zoneLabel: string | null;
  title: string;
}

/** A change that can be applied. `adapted` is built from this element's own data; `example` needs the person's own words. */
export interface GuidanceProposal {
  status: 'adapted' | 'example';
  language: 'html' | 'css';
  before: string;
  after: string;
  explanation: string;
  where: string | null;
  /** For a decision (informative or decorative image): the option this proposal belongs to. */
  choice?: string;
}

/** What to do about one element, written for the cases the catalogue is specific about. */
export interface Guidance {
  specific: boolean;
  validated: boolean;
  what: string;
  evidence: { label: string; value: string }[];
  steps: string[];
  check: string[];
  decision: {
    question: string;
    hint: string;
    options: { id: string; label: string; description: string }[];
  } | null;
  proposals: GuidanceProposal[];
  cms: { id: 'wordpress'; label: string; steps: string[] } | null;
}

/** One time a check found an element on one page and device. */
export interface PageOccurrence {
  /** Stable for the scan: the issue and the position of the example in its list. */
  id: string;
  /** What the team recorded about this element: the same on both devices, and in every analysis. */
  follow: FollowState;
  device: Device;
  selector: string;
  xpath: string | null;
  html: string;
  box: Box | null;
  message: string | null;
  evidence: Record<string, string | number> | null;
  element: ElementName;
  /** Null when the catalogue has nothing specific: the issue's own explanation and fix are then the guidance. */
  guidance: Guidance | null;
  /** The same element on other pages (each its own page, with its own state); null when it is on this page only. */
  sharedWith: {
    identical: boolean;
    /** The page the element is looked after on (the home page when it is one of them) and whether it is this one. */
    owner: { pageKey: string; url: string | null; kind: 'home' | 'first'; isThisPage: boolean };
    /** Where the correction stands across the pages that have it: applied is not verified. */
    progress: GlobalProgress;
    /** Every page that has it, this one included, each with its own record. */
    members: {
      pageKey: string;
      url: string | null;
      lang: string | null;
      tracked: boolean;
      work: WorkStatus | null;
      check: string | null;
    }[];
    pages: { pageKey: string; url: string | null; lang: string | null }[];
  } | null;
}

/** One element that several pages repeat. `identical`: the same code; otherwise only text or addresses change. */
export interface SharedComponent {
  selector: string;
  html: string;
  identical: boolean;
  zone: string | null;
  /** Where it sits on the page, in the language of the screen (header, footer…). */
  zoneLabel?: string | null;
  pageKeys: string[];
  /** The page it is looked after on, and whether that is the home page or only the first of them. */
  ownerKey?: string;
  ownerKind?: 'home' | 'first';
}

export type GlobalProgress =
  | 'sin-aplicar'
  | 'aplicada-en-parte'
  | 'aplicada'
  | 'verificada-en-parte'
  | 'verificada';

/** An issue as found on one page: its elements there, with where they are in the screenshot. */
export interface PageIssue extends Omit<ScanIssue, 'examples'> {
  occurrences: PageOccurrence[];
  /** On this page: distinct elements, appearances and devices; and how many other pages have it. */
  onThisPage: PageStat;
  otherPages: number;
}

/**
 * A page of the website with its latest result: what the analyses found there, the corrections a check verified and
 * the analysis it comes from. One entry per page, however many analyses included it.
 */
export interface SitePage extends ScannedPage {
  scanId: string;
  /** When the analysis that holds the result finished (ISO). */
  analysedAt: string;
  scanType: ScanType;
  /** What the page has pending, in the units of the results; null when it could not be read. */
  found: PageProblems | null;
  /** Highest-priority pending problem on the page, for shareable `?k=` links. */
  topKey: string | null;
  /** Corrections a check confirmed (a correction only declared as done is not one). */
  verified: number;
  /** How many analyses included the page. */
  analyses: number;
}

/** Every page of the website that was ever analysed, once each (not only the pages of the latest analysis). */
export const sitePagesQuery = (siteId: string) =>
  queryOptions({
    queryKey: ['sites', siteId, 'pages', lang()],
    queryFn: () =>
      api<{ pages: SitePage[] }>(`/api/app/sites/${encodeURIComponent(siteId)}/pages?lang=${lang()}`).then(
        (data) => data.pages,
      ),
    networkMode: 'always',
  });

export const pageQuery = (scanId: string, key: string) =>
  queryOptions({
    queryKey: ['scans', scanId, 'pages', key, lang()],
    queryFn: () =>
      api<{
        page: ScannedPage;
        issues: PageIssue[];
        checks: { available: boolean; passed: PassedCheck[] };
        /** Elements of this page that are shared components another page looks after. */
        global: { delegatedElements: number };
      }>(`/api/app/scans/${encodeURIComponent(scanId)}/pages/${encodeURIComponent(key)}?lang=${lang()}`),
  });

/** Where a screenshot of a page is served (only for the organisation's scans). */
export const captureUrl = (scanId: string, key: string, kind: CaptureKind) =>
  captureFile(scanId, `${key}-${kind}.webp`);

/** A screenshot file by the name the API gives it (the overview's thumbnails). */
export const captureFile = (scanId: string, name: string) =>
  `/api/app/scans/${encodeURIComponent(scanId)}/captures/${encodeURIComponent(name)}`;

export interface SiteOverview {
  siteId: string;
  latest: Scan;
  lastDone: Scan | null;
  /** Finished scans, oldest first, with the score of each. */
  history: { id: string; createdAt: string; score: number | null }[];
  /** The home page's thumbnail of the latest scan, while it keeps its screenshots. */
  thumbnail: { scanId: string; name: string } | null;
  /** Settings asked for a new card picture: show that one instead of the analysis thumbnail. */
  cardPreview?: boolean;
  /** What the latest finished analysis leaves pending: problems (one per rule) and the elements behind them. */
  pending: {
    problems: number;
    global: number;
    elements: number;
    /** The engine stopped counting elements of some check: the elements are "at least". */
    capped: boolean;
    /** The problems by priority, one per rule, as the results give them. */
    byPriority: Record<'alta' | 'media' | 'baja', number>;
    /** Checks the automatic analysis cannot decide: a person has to review them (one per rule). */
    review: number;
  } | null;
}

export const overviewQuery = queryOptions({
  queryKey: ['overview'],
  queryFn: () => api<{ sites: SiteOverview[] }>('/api/app/overview').then((data) => data.sites),
  // While a website is being analysed, its card follows it.
  refetchInterval: (query) => (query.state.data?.some((site) => isActive(site.latest)) ? 4000 : false),
});

export const scanIssuesQuery = (scanId: string) =>
  queryOptions({
    queryKey: ['scans', scanId, 'issues', lang()],
    queryFn: () =>
      api<{ issues: ScanIssue[] }>(`/api/app/scans/${encodeURIComponent(scanId)}/issues?lang=${lang()}`).then(
        (data) => data.issues,
      ),
  });

/**
 * The PDF report of a finished scan. The API gets the worker to lay it out, which takes a few seconds;
 * the file is saved with the name the API gives it.
 */
export async function downloadReport(scanId: string): Promise<void> {
  const res = await fetch(`/api/app/scans/${encodeURIComponent(scanId)}/report`, {
    headers: { accept: 'application/pdf' },
  });
  if (!res.ok) {
    const data: unknown = await res.json().catch(() => null);
    const code = (data as { error?: unknown } | null)?.error;
    throw new ApiError(res.status, typeof code === 'string' ? code : null);
  }
  const name = /filename="([^"]+)"/.exec(res.headers.get('content-disposition') ?? '')?.[1];
  const url = URL.createObjectURL(await res.blob());
  const link = document.createElement('a');
  link.href = url;
  link.download = name ?? 'informe-accesibilidad.pdf';
  document.body.append(link);
  link.click();
  link.remove();
  // The download has started by now; the browser may still be reading the file for a moment.
  setTimeout(() => URL.revokeObjectURL(url), 10_000);
}

/** Starts an analysis. `urls` is the custom page list (paid plans only); absent when SiteNorma picks them. */
export const startScan = (siteId: string, type: ScanMode, urls?: string[]) =>
  api<Scan>(`/api/app/sites/${encodeURIComponent(siteId)}/scans`, {
    method: 'POST',
    body: { type, ...(urls && urls.length > 0 ? { urls } : {}) },
  });

/**
 * Completes a partial full scan: re-scans only the pages that failed or were blocked and merges them
 * with the successful ones. Charges only those addresses.
 */
export const completeScan = (scanId: string) =>
  api<Scan>(`/api/app/scans/${encodeURIComponent(scanId)}/complete`, { method: 'POST' });

/** Starts the check of some corrections: only the pages of those elements are analysed. */
export const startCheck = (siteId: string, occurrenceIds: string[]) =>
  api<Scan>(`/api/app/sites/${encodeURIComponent(siteId)}/scans`, {
    method: 'POST',
    body: { type: 'comprobacion', occurrenceIds },
  });

/** What a person has used of the month's pages (the one rule of packages/shared/src/usage.ts). */
export interface Usage {
  plan: string;
  /** Pages per month of the plan; null without a limit. */
  limit: number | null;
  used: number;
  /** Set aside for the analyses that are queued or running. */
  reserved: number;
  /** What is left; null without a limit. */
  remaining: number | null;
  /** When the balance starts again (ISO date). */
  renewsAt: string;
}

/** Plan, limits and monthly usage for «Plan y facturación» (same balance as preparation). */
export interface PlanDetails {
  plan: Plan;
  usage: Usage;
  limits: {
    pagesPerMonth: number | null;
    wpSites: number | null;
    canSelectPages: boolean;
  };
}

export const planQuery = queryOptions({
  queryKey: ['plan'],
  queryFn: () => api<PlanDetails>('/api/app/plan'),
});

/** Paid plans, as the API sells them (packages/shared/src/billing.ts). */
export type PaidPlan = 'pro' | 'agencia';
export type BillingInterval = 'month' | 'year';

export interface Subscription {
  plan: PaidPlan;
  interval: BillingInterval;
  status: string;
  /** End of the period already paid: the renewal, or the end of the plan when it is cancelled. */
  currentPeriodEnd: string | null;
  cancelAtPeriodEnd: boolean;
}

/** Subscription of the signed-in person and what they can buy. Prices are in euro cents, without VAT. */
export interface Billing {
  subscription: Subscription | null;
  /** False for a plan the team assigned by hand: a purchase would not replace it. */
  canSubscribe: boolean;
  prices: Record<PaidPlan, Record<BillingInterval, number>>;
}

/** Null when payments are not available yet (the server has no Stripe): the page then shows the old links. */
export const billingQuery = queryOptions({
  queryKey: ['billing'],
  queryFn: async () => {
    try {
      return await api<Billing>('/api/app/billing');
    } catch (failure) {
      if (failure instanceof ApiError && failure.status === 404) return null;
      throw failure;
    }
  },
});

/** The address of the Stripe page where the person pays; they are sent there. */
export const startCheckout = (plan: PaidPlan, interval: BillingInterval) =>
  api<{ url: string }>('/api/app/billing/checkout', { method: 'POST', body: { plan, interval } });

/** The address of the Stripe portal: change plan or card, billing details, cancel. */
export const openBillingPortal = () => api<{ url: string }>('/api/app/billing/portal', { method: 'POST' });

/** The person is back from the payment: confirm it now instead of waiting for the notice of Stripe. */
export const confirmCheckout = (session: string) =>
  api<{ subscription: Subscription | null }>('/api/app/billing/confirm', {
    method: 'POST',
    body: { session },
  });

/** What the summary before an analysis shows. Asking for it starts nothing and uses nothing. */
export interface Preparation {
  url: string;
  verified: boolean;
  /** An analysis is already queued or running. */
  active: boolean;
  /** Paid plans may choose the addresses; the free plan only gets the automatic selection. */
  canSelectPages: boolean;
  /** Pages already known for this website (home plus past analyses), for the custom list. */
  pages: { url: string; title: string | null }[];
  modes: { id: ScanMode; maxPages: number; needsVerification: boolean }[];
  /** Only when asked for the check of some corrections: the pages and elements it would analyse. */
  check: {
    elements: FollowItem[];
    pages: { key: string; url: string; elements: number; devices: Device[] }[];
    /** Asked for but not checkable (archived, discarded). */
    skipped: number;
    maxPages: number;
    planned: number;
    tooMany: boolean;
  } | null;
  usage: Usage;
}

export const preparationQuery = (siteId: string, check: string[] = []) =>
  queryOptions({
    queryKey: ['sites', siteId, 'preparation', check.join(',')],
    queryFn: () =>
      api<Preparation>(
        `/api/app/sites/${encodeURIComponent(siteId)}/preparation?lang=${lang()}${
          check.length > 0 ? `&check=${check.map(encodeURIComponent).join(',')}` : ''
        }`,
      ),
    // The balance moves with every analysis: never show an old one.
    staleTime: 0,
    gcTime: 0,
  });

export const adminUsersQuery = queryOptions({
  queryKey: ['admin', 'users'],
  queryFn: () => api<{ users: AdminUser[] }>('/api/app/admin/users').then((data) => data.users),
});

export const adminOverviewQuery = (days: OverviewDays) =>
  queryOptions({
    queryKey: ['admin', 'overview', days],
    queryFn: () => api<AdminOverview>(`/api/app/admin/overview?days=${days}`),
  });

export const setUserBanned = (userId: string, banned: boolean) =>
  api<{ banned: boolean }>(`/api/app/admin/users/${encodeURIComponent(userId)}/ban`, {
    method: 'PUT',
    body: { banned },
  });

export const setUserPlan = (userId: string, plan: Plan) =>
  api<{ plan: Plan }>(`/api/app/admin/users/${encodeURIComponent(userId)}/plan`, {
    method: 'PUT',
    body: { plan },
  });

/** Websites a plan can link with the WordPress plugin; null means no limit (packages/shared/src/plans.ts). */
export interface WpConnection {
  id: string;
  site: { id: string; name: string; url: string; verified: boolean };
  organization: string;
  authorizedBy: string;
  authorizedById: string;
  tokenPrefix: string;
  wpVersion: string | null;
  pluginVersion: string | null;
  createdAt: string;
  lastSeenAt: string | null;
}

export interface WpConnections {
  connections: WpConnection[];
  plan: Plan;
  limit: number | null;
  used: number;
}

export interface PluginInfo {
  version: string;
  filename: string;
  sha256: string;
  size: number;
  requiresWp: string;
  requiresPhp: string;
}

export const wpConnectionsQuery = queryOptions({
  queryKey: ['wordpress', 'connections'],
  queryFn: () => api<WpConnections>('/api/app/wordpress/connections'),
});

/** The plugin's version and size; 404 while no zip has been built. */
export const pluginInfoQuery = queryOptions({
  queryKey: ['wordpress', 'plugin'],
  queryFn: () => api<PluginInfo>('/api/app/wordpress/plugin').catch(() => null),
  staleTime: 5 * 60_000,
});

export const unlinkWordPress = (id: string) =>
  api<void>(`/api/app/wordpress/connections/${encodeURIComponent(id)}`, { method: 'DELETE' });

/** What the connect page shows before approving. */
export interface WpPreview {
  url: string;
  host: string;
  plan: Plan;
  limit: number | null;
  used: number;
  known: boolean;
  canLink: boolean;
}

export const wpPreviewQuery = (site: string) =>
  queryOptions({
    queryKey: ['wordpress', 'preview', site],
    queryFn: () => api<WpPreview>(`/api/app/wordpress/preview?site=${encodeURIComponent(site)}`),
    retry: false,
  });

/** Approves linking a WordPress site; the answer says where to send the browser back to the plugin. */
export const authorizeWordPress = (input: {
  site: string;
  callback: string;
  state: string;
  challenge: string;
  name?: string;
}) => api<{ redirect: string }>('/api/app/wordpress/authorize', { method: 'POST', body: input });

export const adminWpConnectionsQuery = queryOptions({
  queryKey: ['admin', 'wordpress'],
  queryFn: () =>
    api<{ connections: WpConnection[] }>('/api/app/admin/wordpress').then((data) => data.connections),
});

export const adminUnlinkWordPress = (id: string) =>
  api<void>(`/api/app/admin/wordpress/${encodeURIComponent(id)}`, { method: 'DELETE' });

// ---------------------------------------------------------------------------------------------------------
// MCP tokens for IDE agents (apps/api/src/mcp.ts, docs/mcp.md).

export interface McpToken {
  id: string;
  name: string;
  tokenPrefix: string;
  userId: string;
  userName: string;
  createdAt: string;
  lastSeenAt: string | null;
}

export interface McpTokens {
  tokens: McpToken[];
  maxPerUser: number;
  mine: number;
  appOrigin: string;
}

export interface McpTokenCreated {
  token: string;
  detail: McpToken;
  config: { mcpServers: Record<string, unknown> };
  /** Paste this into an agent chat. Fetching it returns the setup instructions. The token is in the query. */
  agentUrl: string;
}

export const mcpTokensQuery = queryOptions({
  queryKey: ['mcp', 'tokens'],
  queryFn: () => api<McpTokens>('/api/app/mcp/tokens'),
});

export const createMcpToken = (name?: string) =>
  api<McpTokenCreated>('/api/app/mcp/tokens', {
    method: 'POST',
    body: name ? { name } : {},
  });

export const revokeMcpToken = (id: string) =>
  api<void>(`/api/app/mcp/tokens/${encodeURIComponent(id)}`, { method: 'DELETE' });

// ---------------------------------------------------------------------------------------------------------
// The accessibility statement of a website (apps/api/src/statements.ts).

/** The languages SiteNorma has a statement template for. */
export const STATEMENT_LANGS = ['es', 'ca', 'en', 'fr', 'de'] as const;
export type StatementLang = (typeof STATEMENT_LANGS)[number];
/** The conformity a person can declare: full conformity is not offered, automatic tests cannot show it. */
export const CONFORMITY = ['parcial', 'no-conforme', 'no-evaluado'] as const;
export type Conformity = (typeof CONFORMITY)[number];

export interface StatementData {
  entity: { name: string; taxId: string; address: string; email: string; phone: string };
  site: { name: string; url: string };
  contact: { email: string; url: string; responseDays: number | null };
  conformity: Conformity;
  /** Kept for drafts saved earlier. The degree is calculated; this flag no longer decides it. */
  conformityConfirmed: boolean;
  scope: string;
  enforcement: string;
  exceptions: string;
  preparationNote: string;
}

export interface StatementSection {
  id: string;
  title: string;
  body: string;
}

export type StatementProblem =
  | 'entity'
  | 'address'
  | 'email'
  | 'site'
  | 'contact'
  | 'enforcement'
  | 'conformity'
  | 'blank'
  | 'empty';

export type StatementPublicationMethod = 'wordpress' | 'html' | 'script' | 'link';
/** pendiente: not validated (or changes not validated yet); validada: validated, not on the website; publicada: on it. */
export type StatementStatus = 'pendiente' | 'validada' | 'publicada';

export interface StatementView {
  lang: StatementLang;
  profile: string;
  standard: string;
  data: StatementData;
  sections: StatementSection[];
  problems: StatementProblem[];
  evidence: {
    scanId: string | null;
    scanDate: string | null;
    scanType: string | null;
    pagesAnalysed: number;
    problems: number;
    elements: number;
    openProblems: { title: string; priority: Priority; pages: number; manual: boolean }[];
    manual: { registered: number; total: number };
  };
  /** The evaluation, apart from the document. `conformity` is what the text may say. */
  evaluation: {
    state: 'pendiente' | 'incompleta' | 'completada';
    conformity: Conformity;
    barriers: boolean;
  };
  suggestion: {
    value: Conformity;
    reason: 'no-analysis' | 'problems' | 'automatic-only';
    insufficient: boolean;
  };
  outdated: boolean;
  /** The generated document as it will look on the website (HTML made by the platform, read-only). */
  preview: string | null;
  status: StatementStatus;
  /** The draft differs from the latest validated version. */
  changed: boolean;
  versions: { version: number; publishedAt: string; validatedBy: string }[];
  published: { version: number; publishedAt: string } | null;
  live: {
    method: StatementPublicationMethod;
    version: number;
    at: string;
    applied: boolean;
    pageUrl?: string | null;
  } | null;
  /** The website has a WordPress installation linked. */
  wordpress: boolean;
  /** Languages with an approved version; the embed shows the one that matches the page. */
  embedLangs: StatementLang[];
  delivery: {
    page: string;
    fragment: string;
    script: string;
    snippets: {
      script: string;
      html: string;
      /** Approved HTML per language (for copying without switching the editor language). */
      htmlByLang: Partial<Record<StatementLang, string>>;
      link: string;
    };
  } | null;
}

const statementPath = (siteId: string, lang: StatementLang, rest = '') =>
  `/api/app/sites/${encodeURIComponent(siteId)}/statement/${lang}${rest}`;

export const statementQuery = (siteId: string, lang: StatementLang) =>
  queryOptions({
    queryKey: ['sites', siteId, 'statement', lang],
    queryFn: () => api<StatementView>(statementPath(siteId, lang)),
  });

export interface StatementLanguages {
  languages: {
    lang: StatementLang;
    hasDraft: boolean;
    version: number | null;
    publishedAt: string | null;
    detected: boolean;
  }[];
  /** The languages the pages of the latest analysis say they are in (even without a template). */
  detected: { lang: string; pages: number }[];
  supported: StatementLang[];
}

export const statementLanguagesQuery = (siteId: string) =>
  queryOptions({
    queryKey: ['sites', siteId, 'statements'],
    queryFn: () => api<StatementLanguages>(`/api/app/sites/${encodeURIComponent(siteId)}/statements`),
  });

export const setStatementLanguages = (siteId: string, languages: StatementLang[]) =>
  api<StatementLanguages>(`/api/app/sites/${encodeURIComponent(siteId)}/statements/languages`, {
    method: 'PUT',
    body: { languages },
  });
/** Saves the data and generates the text again from it: the text is never edited. */
export const saveStatementData = (siteId: string, lang: StatementLang, data: StatementData) =>
  api<StatementView>(statementPath(siteId, lang, '/data'), { method: 'PUT', body: data });
export const generateStatement = (siteId: string, lang: StatementLang) =>
  api<StatementView>(statementPath(siteId, lang, '/generate'), { method: 'POST', body: {} });
export const validateStatement = (siteId: string, lang: StatementLang) =>
  api<StatementView>(statementPath(siteId, lang, '/validate'), { method: 'POST', body: { confirmed: true } });
export const publishStatementOn = (siteId: string, lang: StatementLang, method: StatementPublicationMethod) =>
  api<StatementView>(statementPath(siteId, lang, '/publication'), { method: 'POST', body: { method } });
export const restoreStatement = (siteId: string, lang: StatementLang, version: number) =>
  api<StatementView>(statementPath(siteId, lang, `/restore/${version}`), { method: 'POST', body: {} });
export const statementVersionQuery = (siteId: string, lang: StatementLang, version: number) =>
  queryOptions({
    queryKey: ['sites', siteId, 'statement', lang, 'version', version],
    queryFn: () =>
      api<{ version: number; publishedAt: string; validatedBy: string; sections: StatementSection[] }>(
        statementPath(siteId, lang, `/versions/${version}`),
      ),
  });

// The accessibility seal of a website (apps/api/src/seals.ts).
export type SealCriterionId = 'ownership' | 'analysis' | 'noHigh' | 'manual';
export const SEAL_LANGS = ['es', 'ca', 'en', 'fr', 'de'] as const;
export type SealLang = (typeof SEAL_LANGS)[number];
export type SealTheme = 'light' | 'dark';
export type SealSize = 's' | 'm' | 'l';
export type SealState = 'vigente' | 'caducado' | 'suspendido';
export interface SealLook {
  lang?: SealLang;
  theme: SealTheme;
  size: SealSize;
}
export interface SealView {
  status: 'inactivo' | SealState;
  active: boolean;
  activatedAt: string | null;
  /** What has to be met to activate it, and what is only recommended. */
  criteria: { id: SealCriterionId; met: boolean }[];
  recommended: { id: 'statement'; met: boolean }[];
  eligible: boolean;
  stale: boolean;
  expiresAt: string | null;
  rules: { validDays: number; manualMin: number; scanType: string };
  evidence: {
    scanDate: string | null;
    scanType: string | null;
    pagesAnalysed: number;
    counts: { alta: number; media: number; baja: number };
    manual: { registered: number; total: number };
  };
  languages: { detected: SealLang[]; supported: SealLang[]; selected: SealLang };
  sizes: SealSize[];
  /** The website has a WordPress installation linked: the plugin can show the seal in its footer. */
  wordpress: boolean;
  delivery: { page: string; badge: string; snippet: string } | null;
}

/** The look is part of the question: the copy-and-paste HTML is made for it. */
export const sealQuery = (siteId: string, look: SealLook) =>
  queryOptions({
    queryKey: ['sites', siteId, 'seal', look.lang ?? 'auto', look.theme, look.size],
    queryFn: () =>
      api<SealView>(
        `/api/app/sites/${encodeURIComponent(siteId)}/seal?theme=${look.theme}&size=${look.size}${look.lang ? `&lang=${look.lang}` : ''}`,
      ),
  });
/** The image of the badge for the preview (the state asked for, or the one the seal has). */
export const sealBadgeUrl = (siteId: string, look: SealLook & { state?: SealState }) =>
  `/api/app/sites/${encodeURIComponent(siteId)}/seal/badge.svg?theme=${look.theme}&size=${look.size}${look.lang ? `&lang=${look.lang}` : ''}${look.state ? `&state=${look.state}` : ''}`;
export const changeSeal = (siteId: string, action: 'activate' | 'withdraw') =>
  api<SealView>(`/api/app/sites/${encodeURIComponent(siteId)}/seal/${action}`, { method: 'POST', body: {} });
